import React, { useState, useRef, useEffect } from 'react';

const QUARK_SLOTS = [
  { id: 'u1', type: 'up', label: 'u', color: '#4a9eff', mass: 2.3, x: 0.25, y: 0.5 },
  { id: 'u2', type: 'up', label: 'u', color: '#4a9eff', mass: 2.3, x: 0.75, y: 0.25 },
  { id: 'd1', type: 'down', label: 'd', color: '#60b0ff', mass: 4.8, x: 0.75, y: 0.75 },
];

const PROTON_MASS = 938.3; // MeV
const QUARK_MASS_TOTAL = 2.3 + 2.3 + 4.8; // ~9.4 MeV
const QCD_BINDING = PROTON_MASS - QUARK_MASS_TOTAL; // ~928.9 MeV

export default function ProtonBuilder({ mode }) {
  const [assembled, setAssembled] = useState(false);
  const [animStep, setAnimStep] = useState(0);
  const canvasRef = useRef(null);
  const animRef = useRef(null);
  const tRef = useRef(0);
  const isRuach = mode === 'ruach';
  const accentColor = isRuach ? '#ffb060' : '#f0c040';

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const W = canvas.width, H = canvas.height;
    const cx = W / 2, cy = H / 2;

    const draw = () => {
      tRef.current += 0.02;
      const t = tRef.current;
      ctx.clearRect(0, 0, W, H);

      if (!assembled) {
        // Show 3 free quarks
        QUARK_SLOTS.forEach((q, i) => {
          const px = q.x * W + Math.sin(t * 0.8 + i * 1.2) * 6;
          const py = q.y * H + Math.cos(t * 0.6 + i * 0.9) * 6;

          // Color field haze
          ctx.beginPath(); ctx.arc(px, py, 22, 0, Math.PI * 2);
          ctx.fillStyle = q.color + '15'; ctx.fill();
          ctx.beginPath(); ctx.arc(px, py, 14, 0, Math.PI * 2);
          ctx.fillStyle = q.color + '30'; ctx.fill();
          ctx.beginPath(); ctx.arc(px, py, 9, 0, Math.PI * 2);
          ctx.fillStyle = q.color; ctx.fill();

          ctx.font = 'bold 9px Space Mono, monospace';
          ctx.fillStyle = '#fff'; ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
          ctx.fillText(q.label, px, py);

          // Mass label
          ctx.font = '7px Space Mono, monospace';
          ctx.fillStyle = q.color + 'aa';
          ctx.fillText(q.mass + ' MeV', px, py + 20);
        });

        // Instruction
        ctx.font = '10px Space Mono, monospace';
        ctx.fillStyle = 'rgba(255,255,255,0.25)';
        ctx.textAlign = 'center'; ctx.textBaseline = 'bottom';
        ctx.fillText('3 free quarks — total mass: 9.4 MeV', cx, H - 8);

      } else {
        // Assembled proton
        const progress = Math.min(1, animStep / 40);
        const protonR = 30 + progress * 10;

        // Outer glow
        ctx.beginPath(); ctx.arc(cx, cy, protonR + 20, 0, Math.PI * 2);
        ctx.fillStyle = '#4a9eff08'; ctx.fill();
        ctx.beginPath(); ctx.arc(cx, cy, protonR + 8, 0, Math.PI * 2);
        ctx.fillStyle = '#4a9eff12'; ctx.fill();

        // Proton body
        ctx.beginPath(); ctx.arc(cx, cy, protonR, 0, Math.PI * 2);
        ctx.fillStyle = '#4a9eff20'; ctx.fill();
        ctx.strokeStyle = '#4a9eff60'; ctx.lineWidth = 1.5; ctx.stroke();

        // Gluon field lines (animated)
        for (let i = 0; i < 3; i++) {
          const ang = t * (0.6 + i * 0.2) + i * (Math.PI * 2 / 3);
          const x1 = cx + Math.cos(ang) * 12;
          const y1 = cy + Math.sin(ang) * 12;
          const ang2 = ang + Math.PI * 2 / 3;
          const x2 = cx + Math.cos(ang2) * 12;
          const y2 = cy + Math.sin(ang2) * 12;

          ctx.beginPath();
          const cp1x = cx + Math.cos(ang + 0.5) * 22;
          const cp1y = cy + Math.sin(ang + 0.5) * 22;
          ctx.moveTo(x1, y1);
          ctx.quadraticCurveTo(cp1x, cp1y, x2, y2);
          ctx.strokeStyle = `rgba(224,96,255,${0.3 + 0.2 * Math.sin(t * 2 + i)})`;
          ctx.lineWidth = 1; ctx.stroke();
        }

        // Quarks inside
        QUARK_SLOTS.forEach((q, i) => {
          const ang = t * 0.4 + i * (Math.PI * 2 / 3);
          const r = 14;
          const px = cx + Math.cos(ang) * r;
          const py = cy + Math.sin(ang) * r;

          ctx.beginPath(); ctx.arc(px, py, 7, 0, Math.PI * 2);
          ctx.fillStyle = q.color; ctx.globalAlpha = progress; ctx.fill();
          ctx.globalAlpha = 1;
          ctx.font = 'bold 7px Space Mono'; ctx.fillStyle = '#fff';
          ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
          ctx.globalAlpha = progress;
          ctx.fillText(q.label, px, py);
          ctx.globalAlpha = 1;
        });

        // Mass label in center
        if (progress > 0.5) {
          const displayMass = Math.round(QUARK_MASS_TOTAL + (PROTON_MASS - QUARK_MASS_TOTAL) * ((progress - 0.5) * 2));
          ctx.font = 'bold 11px Space Mono';
          ctx.fillStyle = '#fff'; ctx.globalAlpha = (progress - 0.5) * 2;
          ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
          ctx.fillText(displayMass + ' MeV', cx, cy + protonR + 16);
          ctx.globalAlpha = 1;
        }

        if (animStep < 60) setAnimStep(s => s + 1);
      }

      animRef.current = requestAnimationFrame(draw);
    };

    animRef.current = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(animRef.current);
  }, [assembled, animStep]);

  const handleAssemble = () => {
    setAssembled(true);
    setAnimStep(0);
  };

  const handleReset = () => {
    setAssembled(false);
    setAnimStep(0);
    tRef.current = 0;
  };

  return (
    <div>
      <p style={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.75rem', letterSpacing: '0.1em', marginBottom: '1rem', fontFamily: 'Space Mono', textTransform: 'uppercase' }}>
        {isRuach ? 'The first binding — three utterances become one' : 'Proton builder — where does the mass come from?'}
      </p>

      <canvas ref={canvasRef} width={280} height={200}
        style={{ width: '100%', maxWidth: '280px', display: 'block', margin: '0 auto' }} />

      <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1rem', justifyContent: 'center' }}>
        {!assembled ? (
          <button onClick={handleAssemble} style={{
            padding: '0.7rem 1.5rem', borderRadius: '8px', cursor: 'pointer',
            background: `${accentColor}20`, border: `1px solid ${accentColor}50`,
            color: accentColor, fontFamily: 'Space Mono', fontSize: '0.78rem',
            letterSpacing: '0.08em', textTransform: 'uppercase',
          }}>
            {isRuach ? 'Bind the utterances' : 'Assemble proton'}
          </button>
        ) : (
          <button onClick={handleReset} style={{
            padding: '0.7rem 1.25rem', borderRadius: '8px', cursor: 'pointer',
            background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.15)',
            color: 'rgba(255,255,255,0.5)', fontFamily: 'Space Mono', fontSize: '0.75rem',
          }}>
            Reset
          </button>
        )}
      </div>

      {/* Mass breakdown */}
      <div style={{ marginTop: '1.25rem', borderRadius: '10px', overflow: 'hidden', border: '1px solid rgba(255,255,255,0.08)' }}>
        <div style={{ padding: '0.875rem 1rem', background: 'rgba(255,255,255,0.04)', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
          <div style={{ fontSize: '0.65rem', fontFamily: 'Space Mono', textTransform: 'uppercase', letterSpacing: '0.1em', color: 'rgba(255,255,255,0.3)', marginBottom: '0.25rem' }}>
            {isRuach ? 'Where the weight comes from' : 'Mass composition of the proton'}
          </div>
          <div style={{ fontSize: '1rem', fontWeight: 700, fontFamily: 'Syne', color: '#fff' }}>938.3 MeV total</div>
        </div>

        {[
          { label: isRuach ? 'Ruach-given weight (up quarks)' : 'Up quarks (Higgs mass)', value: '4.6 MeV', pct: 0.5, color: '#4a9eff', detail: '0.5%' },
          { label: isRuach ? 'Ruach-given weight (down quark)' : 'Down quark (Higgs mass)', value: '4.8 MeV', pct: 0.5, color: '#60b0ff', detail: '0.5%' },
          { label: isRuach ? 'Hidden binding (gluon confinement)' : 'QCD binding energy (gluons)', value: '~928.9 MeV', pct: 99, color: '#e060ff', detail: '~99%' },
        ].map(row => (
          <div key={row.label} style={{ padding: '0.75rem 1rem', borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
              <span style={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.65)' }}>{row.label}</span>
              <span style={{ fontFamily: 'Space Mono', fontSize: '0.72rem', color: row.color }}>{row.value}</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div style={{ flex: 1, height: '4px', background: 'rgba(255,255,255,0.08)', borderRadius: '2px' }}>
                <div style={{ width: `${row.pct}%`, height: '100%', background: row.color, borderRadius: '2px', opacity: 0.7 }} />
              </div>
              <span style={{ fontSize: '0.65rem', fontFamily: 'Space Mono', color: 'rgba(255,255,255,0.3)', width: '32px', textAlign: 'right' }}>{row.detail}</span>
            </div>
          </div>
        ))}

        <div style={{ padding: '0.875rem 1rem' }}>
          <p style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.5)', lineHeight: 1.7, margin: 0 }}>
            {isRuach
              ? '"In him all things hold together." — Colossians 1:17. Nearly everything you are made of — your bones, your blood, every atom of your body — gets its weight not from the Ruach field directly, but from the hidden binding of the gluon. The Ruach gives form to quarks. The gluon\'s confinement gives weight to matter.'
              : 'The Higgs field gives quarks only ~1% of the proton\'s mass. The other 99% comes from the kinetic energy of quarks moving at near-light speed inside the proton, plus the gluon binding energy — pure QCD. E=mc² in action: energy becomes mass through confinement.'}
          </p>
        </div>
      </div>
    </div>
  );
}
